import {
  Component,
  Output,
  ViewEncapsulation,
  Input,
  EventEmitter,
  SimpleChanges,
} from "@angular/core";
import { faArrowToBottom } from "@fortawesome/pro-solid-svg-icons";
import {
  getUserSettingDetails,
  getTickersId,
  updateUserSettingDetails,
  getExtraLogDetails,
  getUpdatedCompanyIdDifference,
  getTickersCompanyId,
  getSelectedRatiosId,
  getOtherPeriods,
} from "../valuation-page-utils";
import {
  ChartsState,
  UserSettingObject,
  ValuationPeriodState,
} from "../valuation-page.model";
import {
  CHARTS_TAB,
  VALUATION_LOG_ACTION_FEATURES,
  VALUATION_PAGE,
  CHART_LEGEND_COLORS,
  CHART_LEGEND_SHAPES,
  SCALE_TYPE_LIST,
} from "../valuation-page.constant";
import {
  getSelectedPeriods,
  getPeriodTypeDataId,
} from "src/app/utils/period-utils";
import { NzModalService, NzModalRef } from "ng-zorro-antd/modal";
import {
  PeriodUserSettingObj,
  getSelectedPeriodType,
  createSelectedPeriodLabel,
} from "@va/core-library/period-filter";
import { ValuationPageDataService } from "../valuation-page-data.service";
import { AppService } from "src/app/app.service";
import * as Highcharts from "highcharts";
import { CompsTickerModalComponent } from "../../comps-page/comps-ticker-modal/comps-ticker-modal.component";
import { LineItemModalComponent } from "src/app/shared/line-item-modal/line-item-modal.component";
@Component({
  selector: "app-valuation-page-charts",
  templateUrl: "./valuation-page-charts.component.html",
  styleUrls: ["./valuation-page-charts.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class ValuationPageChartsComponent {
  @Input() currentTicker: string;
  @Input() currentCompany: number;
  @Input() isMetaReady = false;
  @Input() state: ChartsState;
  @Input() watchlistData: Array<any> = [];
  @Input() recentlyVisitedCompanies: Array<any> = [];
  @Input() suggestedPeers: Array<any> = [];
  @Input() isTickerFilterDataReady = false;
  @Input() isUserPeriodCalSettingsInProgress = false;

  @Output() onPeersChange: EventEmitter<any> = new EventEmitter();
  @Output() onPeriodChange: EventEmitter<any> = new EventEmitter();
  @Output() saveRatios = new EventEmitter();
  @Output() onPeersChanged = new EventEmitter();
  @Output() onRevisionChanged = new EventEmitter();
  @Output() onPeriodCalculationChange: EventEmitter<any> = new EventEmitter();
  @Output() showSnackBarMessage: EventEmitter<any> = new EventEmitter();
  @Output() onScaleTypeFilterChange = new EventEmitter();

  ratioLineItemsData: any;
  suggestedRatioLines;
  isLineItemDataReady = false;
  periodsWithAvailableData;

  chartOptions;

  snackbarInfo: { message: string; type: string } = {
    message: "",
    type: "info",
  };

  selectedTickerIds = [];
  scaleTypeList = SCALE_TYPE_LIST;
  downloadOptVisible = false;
  isUserSettingsInProgress = false;
  isTickerUpdationInProgress = false;
  userSettingDetails: UserSettingObject;
  tickersModal: NzModalRef;
  selectedChart = "charts-page-revision";
  chartLink = "";
  chartName;
  isVisibleEditTickerModal = false;
  isVisibleEditLiModal = false;
  chartInstance: Highcharts.Chart;
  chartInfoBottom = "66px";
  chartReady = false;

  //icons
  faArrowToBottom = faArrowToBottom;

  currentTabId = CHARTS_TAB.id;

  lineItemMaxLimit = 2;
  selectedLineItemCount = 0;
  selectedRatioId;
  lineItemsModal: NzModalRef;

  constructor(
    private modalService: NzModalService,
    private dataService: ValuationPageDataService,
    private appService: AppService,
  ) {}

  ngOnChanges(changes: SimpleChanges) {
    // update ratio line item data on ticker change
    if (
      this.isMetaReady &&
      changes.state &&
      changes.state.currentValue &&
      changes.state.previousValue &&
      changes.state.currentValue.ticker_info !==
        changes.state.previousValue.ticker_info
    ) {
      this.getRatioLineItemData();
      if (changes.state.currentValue.ticker_info) {
        this.selectedTickerIds = getTickersId(
          changes.state.currentValue.ticker_info,
        );
      }
      this.getPeriodsWithAvailableData(this.selectedTickerIds);
    }

    if (changes.state && changes.state.currentValue) {
      //react to changes in state pushed from page component
      this.chartReady = false;
      this.createChartOptions();
    }

    this.chartName = `${this.currentTicker} vs Peers - Valuation Revision`;

    // Set userSetting obj on meta completion
    if (
      changes.isMetaReady &&
      changes.isMetaReady.currentValue &&
      changes.isMetaReady.currentValue !== changes.isMetaReady.previousValue
    ) {
      if (
        this.state &&
        this.state.ratios &&
        this.state.period &&
        this.state.revision &&
        this.state.scaleType &&
        this.state.ticker_info
      ) {
        this.userSettingDetails = getUserSettingDetails({
          tabType: this.currentTabId,
          currentTicker: this.currentTicker,
          ratios: this.state.ratios,
          periods: this.state.period,
          tickers: this.state.ticker_info,
          revision: this.state.revision,
          scaleType: this.state.scaleType,
        });
      }
      if (this.state && this.state.ratios) {
        this.selectedRatioId =
          getSelectedRatiosId(this.state.ratios.ratio_details) || [];
        this.selectedLineItemCount = this.selectedRatioId.length;
      }
    }
  }

  /**
   * It calls the get_std_line_items api and formats the response
   */
  getRatioLineItemData() {
    this.isLineItemDataReady = false;
    const tickerIds = getTickersId(this.state.ticker_info);
    this.dataService.getStdLineItems(tickerIds).subscribe((res) => {
      this.ratioLineItemsData = res.data.filter(
        (d) => d.tab_short_name === "SL" || d.tab_short_name === "VR",
      );
      if (this.state && this.state.ratios) {
        this.selectedRatioId =
          getSelectedRatiosId(this.state.ratios.ratio_details) || [];
        this.selectedLineItemCount = this.selectedRatioId.length;
      }
      const _suggestedLines: Array<any> = res.data.filter(
        (d) => d.tab_short_name === "SL",
      );
      this.suggestedRatioLines = _suggestedLines.length
        ? _suggestedLines[0].tab_sli_data.map((l) => {
            return {
              ...l,
              disabled: false,
              label: l.name,
              isLeaf: true,
              level: 2,
              header: false,
              sliid: l.sli_id,
              line_count: l.line_count,
              id: l.uniquesliid,
            };
          })
        : [];

      if (this.suggestedRatioLines.length > 0) {
        this.suggestedRatioLines = [
          {
            suggestion_header: "Suggestions: ",
            isLeaf: true,
            disabled: true,
          } as any,
        ].concat(this.suggestedRatioLines);
      } else {
        this.suggestedRatioLines = [
          {
            suggestion_header: "No suggestion found ",
            isLeaf: true,
            disabled: true,
          } as any,
        ].concat(this.suggestedRatioLines);
      }
      this.isLineItemDataReady = true;
    });
  }

  /**
   * It returns the require information for ratio line in a object format
   * @return lineInfo object required for chart plotter
   */
  getLineInfo() {
    const result = {};
    const selectedRatioItems =
      this.state.ratios && this.state.ratios.ratio_details
        ? this.state.ratios.ratio_details.filter((item) => item.ratio.selected)
        : [];
    selectedRatioItems.map((el) => {
      const sliId = el.ratio.usliid || el.ratio.sliid || el.ratio.el.sli_id;
      const lineInFo = {
        ...el.ratio,
        sli_id: sliId,
        name: el.ratio.label,
        isValuationRatio: true,
        selected: true,
        magnitude: el.ratio.mag,
      };
      result[sliId] = lineInFo;
      return lineInFo;
    });
    return result;
  }

  /**
   * It creates chart options which are required for ChartPlotter component to create chart
   * It contains information which is required to plot the highcharts
   */
  createChartOptions() {
    if (this.state) {
      this.chartOptions = {
        periods: getSelectedPeriods(this.state.period, false),
        otherPeriods: getOtherPeriods(this.state.period),
        periodType: getPeriodTypeDataId(this.state.period),
        lines:
          this.state.ratios && this.state.ratios.ratio_details
            ? getSelectedRatiosId(this.state.ratios.ratio_details).slice(
                0,
                this.lineItemMaxLimit,
              )
            : [],
        lineInfo: this.getLineInfo(),
        scaleType: this.state.scaleType === "Absolute" ? 1 : 2,
        scaleTypeValue: this.state.scaleType,
        operation: "fetchdata",
        tickers: getTickersId(this.state.ticker_info),
        selectedTickersInfo: this.state.ticker_info,
        seriesInfo: {},
        hoveredTicker: "",
        currency: this.state.currency,
        revision: this.state.revision,
        chartTypeId: "3",
        legendColors: CHART_LEGEND_COLORS,
        legendShapes: CHART_LEGEND_SHAPES,
        isSecondaryAxis: true,
        chartName: this.chartName,
      };

      if (this.chartOptions.lines) {
        this.selectedLineItemCount = this.chartOptions.lines.length;
      }
    }
  }

  /**
   * Executes on click of Download Chart.
   * It uses chartInstance from HighChart and performs the action depending on download type
   * @param {number} downloadType - type of download
   */
  downloadValue(downloadType) {
    this.downloadOptVisible = false;
    let downloadTypeName = "";
    switch (downloadType) {
      case 0:
        downloadTypeName = "Download_PNG";
        break;
      case 1:
        downloadTypeName = "Download_JPEG";
        break;
      case 2:
        downloadTypeName = "Download_PDF";
        break;
      case 3:
        downloadTypeName = "Download_SVG";
        break;
      case 4:
        downloadTypeName = "Print_Chart";
        break;
      default:
        downloadTypeName = "Download_PNG";
    }
    this.appService.logUserAction(
      {},
      "",
      "",
      "VALUATION_CHARTS_DOWNLOAD",
      {
        lines: this.chartOptions.lines,
        tickers: this.selectedTickerIds,
        downloadInfo: downloadTypeName,
        tab_name: CHARTS_TAB.label,
      },
      "",
      VALUATION_PAGE,
    );
    this.chartInstance.options.title.text = this.chartName;
    switch (downloadType) {
      case 0:
        this.chartInstance.exportChart(
          {
            type: "image/png",
            filename: this.chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 1:
        this.chartInstance.exportChart(
          {
            type: "image/jpeg",
            filename: this.chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 2:
        this.chartInstance.exportChart(
          {
            type: "application/pdf",
            filename: this.chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 3:
        this.chartInstance.exportChart(
          {
            type: "image/svg+xml",
            filename: this.chartName,
            sourceWidth: 1920,
            sourceHeight: 1080,
          },
          this.chartInstance.options,
        );
        break;
      case 4:
        this.chartInstance.print();
        break;
      default:
        break;
    }
  }

  /**
   * Executes on click of Export Chart.
   * It uses chartInstance from HighChart and creates the excel sheet and download it on user's system
   */
  onExport() {
    let name = "";
    const xlsxRows = [
      [
        { type: "string", value: "Chart Name" },
        { type: "string", value: this.chartName },
      ],
      [
        { type: "string", value: "Currency" },
        { type: "string", value: this.state.currency },
      ],
      [
        { type: "string", value: "Period" },
        { type: "string", value: createSelectedPeriodLabel(this.state.period) },
      ],
      [
        { type: "string", value: "Rebase to 100" },
        {
          type: "string",
          value: this.state.scaleType,
        },
      ],
      [
        { type: "string", value: "" },
        { type: "string", value: "" },
      ],
    ];
    if (this.state.scaleType !== "Index") {
      xlsxRows.splice(4, 0, [
        { type: "string", value: "" },
        { type: "string", value: `All numbers in M unless stated otherwise` },
      ]);
    }

    // rows = [],
    // headerRow = [];
    xlsxRows.splice(3, 0, [
      { type: "string", value: "Revision History" },
      {
        type: "string",
        value: this.state.revision.startsWith("custom")
          ? this.state.revision.split("|")[1]
          : this.state.revision.replace("m", " months"),
      },
    ]);
    let row = [];
    this.selectedTickerIds.map((ticker, index) => {
      const rows = [];
      rows.push(
        { type: "string", value: ticker },
        { type: "string", value: "Period" },
      );
      xlsxRows.push([]);
      xlsxRows.push(rows);
      this.chartInstance.options.series.map((item) => {
        if (item.name !== "") {
          // row = [];
          if (item["custom"].isLineItem) {
            row = [];
            const magnitude = `in M`;
            row.push({
              type: "string",
              value: `${item.name}${magnitude}`,
            });
          } else {
            if (item.name.split("-")[0] === this.selectedTickerIds[index]) {
              row.length ? row.splice(1) : null;
              row.push({
                type: "string",
                value: item.name.split("-").slice(1).join("-"),
              });
              const dateRow = [
                { type: "string", value: "" },
                { type: "string", value: "" },
              ];
              item["data"].length &&
                item["data"].map((d) => {
                  const xDate = Highcharts.dateFormat("%d-%b-%Y", d.x);
                  dateRow.push({ type: "string", value: xDate });
                  const type = typeof d.y === "number" ? "number" : "string";
                  row.push({ type: type, value: d.y });
                  return d;
                });
              xlsxRows.push(dateRow);
              xlsxRows.push([...row]);
            }
          }
          // if(rowData.length){
          // rowData.map(d => row.push(d));
          // dataRows.push(rowData);
          // }
        }
        return item;
      });
      return ticker;
    });

    name = this.chartName;

    window.zipcelx({
      filename: name,
      sheet: {
        data: xlsxRows,
      },
    });
    this.appService.logUserAction(
      {},
      "",
      "",
      "CHARTS_DOWNLOAD",
      {
        lines: this.chartOptions.lines,
        tickers: this.selectedTickerIds,
        downloadInfo: "Export_Value",
      },
      "",
      "CHARTS_PAGE",
    );
  }

  /**
   * Executes on click of Edit ticker button of ticker filter.
   * It creates Edit ticker modal with required data and opens it
   */
  handleEditTickerClick() {
    const tickerInfo =
      this.state && this.state.ticker_info ? [...this.state.ticker_info] : [];
    this.tickersModal = this.modalService.create({
      nzTitle: "Edit Tickers",
      nzContent: CompsTickerModalComponent,
      nzMaskClosable: false,
      nzComponentParams: {
        addRowTableData: tickerInfo,
        watchlists: this.watchlistData,
        recentlyVisitedCompanies: this.recentlyVisitedCompanies,
        maxHeight: "340px",
        type: "edit",
        mptApplicable: false,
        maxTickers: 10,
      },
      nzWrapClassName: "comps-modal-container vertical-center-modal",
      nzFooter: [
        {
          label: "Cancel",
          type: "default",
          onClick: (): void => {
            this.tickersModal.destroy();
          },
        },
        {
          label: "Update",
          type: "primary",
          class: "update-btn",
          onClick: (data): void => {
            this.updateTickers(data.modalData);
          },
          disabled: (data) => {
            return data.modalData.length > 10 || data.modalData.length === 0;
          },
        },
      ],
    });
  }

  /**
   * It updates the peer companies data.
   * It posts the updated peer to userSetting api
   * It emits onPeersChange which is useful for updating the state object
   * @param {array} ticker - array of all selected tickers
   * @param {object} addedTicker - current selected ticker
   */
  updateTickers(tickers, addedTicker?: { ticker: string; [x: string]: any }) {
    this.isUserSettingsInProgress = true;
    const updatedTickers = tickers.map((item) => ({
      ...item,
      tickerDeleteDisabled: item.ticker === this.currentTicker,
    }));
    this.userSettingDetails = updateUserSettingDetails(
      this.userSettingDetails,
      {
        tickers: getTickersCompanyId(updatedTickers),
      },
    );
    const existingTickers = [...this.state.ticker_info];

    this.dataService
      .saveUserSettings(this.userSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.onPeersChanged.emit(updatedTickers);
          // Show toast message once ticker is added to the table
          if (addedTicker && addedTicker.ticker) {
            this.snackbarInfo.message = `${addedTicker.ticker} has been added to the Chart.`;
            this.snackbarInfo.type = "success";
            this.showSnackBarMessage.emit(this.snackbarInfo);
          }

          this.appService.logUserAction(
            {},
            this.currentTicker,
            "",
            VALUATION_LOG_ACTION_FEATURES.addTicker,
            {
              ...getExtraLogDetails(
                this.currentCompany,
                CHARTS_TAB.label,
                this.userSettingDetails,
                this.state.period_calculation,
              ),
              added_ticker: getUpdatedCompanyIdDifference(
                existingTickers,
                updatedTickers,
              ),
            },
            "",
            VALUATION_PAGE,
          );
        }
        this.isUserSettingsInProgress = false;
      });

    if (this.tickersModal) {
      this.destroyTickerModal();
    }
  }

  /**
   * It destroys Edit ticker modal
   */
  destroyTickerModal(): void {
    this.tickersModal.destroy();
  }

  /**
   * It executes when we select a ticker from inline search ticker filter
   * It checks if the ticker is already present or not. If ticker is present it shows Info alert if not it add the ticker to the list
   * It emits onPeersChange which is useful for updating the state object
   * @param {object} tickerData - selected ticker object
   */
  handleSearchTickerSelection(tickerData) {
    const { value } = tickerData;
    if (this.selectedTickerIds.includes(value.ticker)) {
      this.snackbarInfo.message = "This ticker is already present.";
      this.snackbarInfo.type = "info";
      this.showSnackBarMessage.emit(this.snackbarInfo);
    } else {
      value["cid"] = value.companyid || value.cid;
      value["name"] = value.n || value.name || value.full_name;
      this.updateTickers([...this.state.ticker_info, value], value);
    }
  }

  /**
   * Executes on click of Period filter popup's Apply button.
   * It posts the selected period setting to user setting api
   * It emits `onPeriodChange` with selected periodData
   * @param {{updatedPeriodData: ValuationPeriodState, periodUserSettingData: PeriodUserSettingObj}} periodData: selected values from period filter popup
   */
  handlePeriodApply(periodData: {
    updatedPeriodData: ValuationPeriodState;
    periodUserSettingData: PeriodUserSettingObj;
  }) {
    const { updatedPeriodData, periodUserSettingData } = periodData;

    this.isUserSettingsInProgress = true;

    this.userSettingDetails = updateUserSettingDetails(
      this.userSettingDetails,
      periodUserSettingData,
    );

    this.dataService
      .saveUserSettings(this.userSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.onPeriodChange.emit(updatedPeriodData);
          this.appService.logUserAction(
            {},
            this.currentTicker,
            "",
            VALUATION_LOG_ACTION_FEATURES.updatePeriod,
            getExtraLogDetails(
              this.currentCompany,
              CHARTS_TAB.label,
              this.userSettingDetails,
              this.state.period_calculation,
            ),
            "",
            VALUATION_PAGE,
          );
        }
        this.isUserSettingsInProgress = false;
      });
  }

  /**
   * This function is fired when the save Revision event is fired from the revision history filter component.
   * It updates the revision in the state and calls the saveusersettings api to save it.
   * @param item selected revision value
   */
  onRevisionChange(item: string) {
    this.isUserSettingsInProgress = true;
    this.userSettingDetails = updateUserSettingDetails(
      this.userSettingDetails,
      {
        revision: item,
      },
    );

    this.dataService
      .saveUserSettings(this.userSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.onRevisionChanged.emit(item);
          this.appService.logUserAction(
            {},
            this.currentTicker,
            "",
            VALUATION_LOG_ACTION_FEATURES.updateRevision,
            getExtraLogDetails(
              this.currentCompany,
              CHARTS_TAB.label,
              this.userSettingDetails,
              this.state.period_calculation,
            ),
            "",
            VALUATION_PAGE,
          );
          this.isUserSettingsInProgress = false;
        }
      });
  }

  /**
   * This function is fired when the save Revision event is fired from the rebase to 100 filter component.
   * It updates the scaleType in the state and calls the saveusersettings api to save it.
   * @param item selected scaleType value
   */
  onScaleTypeChange(item: number) {
    this.isUserSettingsInProgress = true;
    this.userSettingDetails = updateUserSettingDetails(
      this.userSettingDetails,
      {
        scaleType: item,
      },
    );

    this.dataService
      .saveUserSettings(this.userSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.onScaleTypeFilterChange.emit(item);
          this.appService.logUserAction(
            {},
            this.currentTicker,
            "",
            VALUATION_LOG_ACTION_FEATURES.updateScaleType,
            getExtraLogDetails(
              this.currentCompany,
              CHARTS_TAB.label,
              this.userSettingDetails,
              this.state.period_calculation,
            ),
            "",
            VALUATION_PAGE,
          );
          this.isUserSettingsInProgress = false;
        }
      });
  }

  /**
   * It updates the flag when chart data is ready
   */
  chartDataSet() {
    this.chartReady = true;
  }

  /**
   * It is executes on change of chart instance
   * Here we are using the chart instance to determine the bottom space, so that we can place the custom info message
   * @param {Highcharts.Chart} chartInstance: instance of high-chart
   */
  onChartInstance(chartInstance: Highcharts.Chart) {
    if (chartInstance) {
      this.chartInstance = chartInstance;
      this.chartInfoBottom = `${
        document.getElementsByClassName("highcharts-legend-box")[0]["height"][
          "baseVal"
        ]["value"] + 12
      }px`;
    }
  }

  /**
   * It creates url to the chart page
   * The url has ticker, lined id, period, other period, revision, chart type id details
   */
  createEditLink() {
    if (this.chartOptions) {
      this.chartLink = "/charts/current?hideVsPeers=true";
      const periodTypeId = this.state.period
        ? getSelectedPeriodType(this.state.period.type)
        : "";
      if (this.chartOptions.tickers) {
        this.chartLink += `&ticker=${this.chartOptions.tickers.join(
          "&ticker=",
        )}`;
      }
      if (this.chartOptions.periods && this.chartOptions.periods.length) {
        this.chartLink += `&periods=${this.chartOptions.periods.join(
          "&periods=",
        )}&period_type=${periodTypeId}`;
      }
      if (
        this.chartOptions.otherPeriods &&
        this.chartOptions.otherPeriods.length
      ) {
        this.chartLink += `&other_period=${this.chartOptions.otherPeriods.join(
          "&other_period=",
        )}`;
      }
      if (this.chartOptions) {
        this.chartLink += `&is_annual_selected=${!!(
          this.chartOptions.periods && this.chartOptions.periods.length
        )}`;
      }

      if (this.chartOptions.lines) {
        this.chartLink += `&lineId=${this.chartOptions.lines.join("&lineId=")}`;
      }
      this.chartLink += `&revision=${
        this.chartOptions.revision || "3m"
      }&chartTypeId=${3}&chart_name=${this.chartName}`;
    }
  }

  /**
   * It executes on the search and selection of line item
   * It replace this line item with existing one
   * @param {object} e: selected line item
   */
  onSearchedLi(e) {
    if (!e) {
      return;
    }
    const selectedRatioLineIds = this.state.ratios
      ? getSelectedRatiosId(this.state.ratios.ratio_details).slice(
          0,
          this.lineItemMaxLimit,
        )
      : [];

    const value = { ...e, parent: undefined, sliid: e.sliid || e.sli_id };
    const alreadyExist = selectedRatioLineIds.includes(value.sliid);

    if (!alreadyExist) {
      const allSelectedLineIds = [value.sliid];
      this.isUserSettingsInProgress = true;

      //update the selected check of ratio item
      const updatedRatios = this.state.ratios.ratio_details.map((item) => {
        const ratioItem = item.ratio;
        if (ratioItem.usliid === value.sliid) {
          item.ratio.selected = true;
        } else {
          item.ratio.selected = false;
        }
        return item;
      });

      this.userSettingDetails = updateUserSettingDetails(
        this.userSettingDetails,
        {
          selectedRatios: allSelectedLineIds,
        },
      );
      this.dataService
        .saveUserSettings(this.userSettingDetails)
        .subscribe((response: { status: string; data }) => {
          if (response.status === "success") {
            this.saveRatios.emit({
              ratios: updatedRatios,
              include: this.state.ratios.include,
            });

            if (updatedRatios) {
              this.selectedRatioId = getSelectedRatiosId(updatedRatios) || [];
              this.selectedLineItemCount = this.selectedRatioId.length;
            }

            this.snackbarInfo.message = `${value.label} has been added to the table`;
            this.snackbarInfo.type = "success";
            this.showSnackBarMessage.emit(this.snackbarInfo);

            this.appService.logUserAction(
              {},
              this.currentTicker,
              "",
              VALUATION_LOG_ACTION_FEATURES.updateRatio,
              getExtraLogDetails(
                this.currentCompany,
                CHARTS_TAB.label,
                this.userSettingDetails,
                this.state.period_calculation,
              ),
              "",
              VALUATION_PAGE,
            );
            this.isUserSettingsInProgress = false;
          }
        });
    } else {
      this.snackbarInfo.message = "This line item is already present.";
      this.snackbarInfo.type = "info";
      this.showSnackBarMessage.emit(this.snackbarInfo);
    }
  }

  /**
   * It adds line items selected from the edit modal to the list
   * @param {object} data: selected data from line item modal
   */
  updateEditModalLineItems(data) {
    const { modalData } = data;

    this.isUserSettingsInProgress = true;
    const selectedRatioItems = modalData.map(
      (item) => item.uniquesliid || item.sliid || item.sli_id,
    );

    //update the selected check of ratio item
    const updatedRatios = this.state.ratios.ratio_details.map((item) => {
      const ratioItem = item.ratio;
      item.ratio.selected = selectedRatioItems.includes(ratioItem.usliid);
      return item;
    });

    this.userSettingDetails = updateUserSettingDetails(
      this.userSettingDetails,
      {
        selectedRatios: selectedRatioItems,
      },
    );

    this.lineItemsModal.destroy();

    this.dataService
      .saveUserSettings(this.userSettingDetails)
      .subscribe((response: { status: string; data }) => {
        if (response.status === "success") {
          this.saveRatios.emit({
            ratios: updatedRatios,
            include: this.state.ratios.include,
          });

          if (updatedRatios) {
            this.selectedRatioId = getSelectedRatiosId(updatedRatios) || [];
            this.selectedLineItemCount = this.selectedRatioId.length;
          }

          this.appService.logUserAction(
            {},
            this.currentTicker,
            "",
            VALUATION_LOG_ACTION_FEATURES.updateRatio,
            getExtraLogDetails(
              this.currentCompany,
              CHARTS_TAB.label,
              this.userSettingDetails,
              this.state.period_calculation,
            ),
            "",
            VALUATION_PAGE,
          );
          this.isUserSettingsInProgress = false;
        }
      });
  }

  /**
   * It executes on the click of Edit button of lien item filter
   * It creates the Line item modal withe required information and opens it.
   */
  handleEditLineItemsButtonClick() {
    const selectedRatioItems = this.state.ratios.ratio_details
      .filter((item) => item.ratio.selected)
      .map((el) => {
        const sliId = el.ratio.usliid || el.ratio.sliid || el.ratio.el.sli_id;
        return {
          ...el.ratio,
          sli_id: sliId,
          name: el.ratio.label,
          is_valuation_ratio: true,
        };
      })
      .slice(0, this.lineItemMaxLimit);
    this.lineItemsModal = this.modalService.create({
      nzTitle: "Edit Line Items",
      nzOkLoading: true,
      nzContent: LineItemModalComponent,
      nzMaskClosable: false,
      nzComponentParams: {
        addColTableData: selectedRatioItems,
        stdLineItems: this.ratioLineItemsData,
        max: this.lineItemMaxLimit,
        maxHeight: "290px",
        selectedPeriod: [],
        tickers: [this.currentTicker],
        createSource: "consensus",
        ratioDenomData: this.state.ratios.include,
      },
      nzWrapClassName: "comps-modal-container vertical-center-modal",
      nzFooter: [
        {
          label: "Cancel",
          type: "default",
          onClick: (): void => {
            this.lineItemsModal.destroy();
          },
        },
        {
          label: "Update",
          type: "primary",
          class: "update-btn",
          nzClassName: (data) => {
            return data.modalData.length > data.max ? "disable" : "";
          },
          onClick: (data): void => {
            this.updateEditModalLineItems(data);
          },
          disabled: (data) => {
            return (
              data.modalData.length > data.max || data.modalData.length === 0
            );
          },
        },
      ],
    });
  }

  getPeriodsWithAvailableData(tickerIds) {
    this.appService
      .getPeriodsWithData(tickerIds)
      .subscribe((response: { status; dataAvailable }) => {
        if (response.status === "success") {
          this.periodsWithAvailableData = response.dataAvailable;
        }
      });
  }
}
